import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const initialFormState = {
  fullName: '',
  email: '',
  company: '',
  website: '',
  audienceDescription: '',
  promotionChannels: '',
  promotionPlan: '',
  audienceSize: '',
  timeframe: '',
  additionalNotes: ''
};

const AffiliateApplicationPage = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.fullName.trim()) {nextErrors.fullName = 'Full name is required.';}
    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = 'Enter a valid email address.';
    }
    if (!formData.company.trim()) {nextErrors.company = 'Company or organization is required.';}
    if (!formData.audienceDescription.trim()) {nextErrors.audienceDescription = 'Describe your audience.';}
    if (!formData.promotionPlan.trim()) {nextErrors.promotionPlan = 'Share how you plan to promote Alfred Voice.';}
    if (!formData.audienceSize.trim()) {nextErrors.audienceSize = 'Estimate your monthly audience reach.';}
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please review the highlighted fields and try again.');
      return;
    }

    const subject = `Alfred Voice Affiliate Application – ${formData.fullName}`;
    const bodyLines = [
      `Name: ${formData.fullName}`,
      `Email: ${formData.email}`,
      `Company / Organization: ${formData.company}`,
      `Website / Primary URL: ${formData.website || 'N/A'}`,
      `Audience size / reach estimate: ${formData.audienceSize}`,
      `Audience description: ${formData.audienceDescription}`,
      `Primary channels: ${formData.promotionChannels || 'N/A'}`,
      `Promotion timeframe: ${formData.timeframe || 'N/A'}`,
      '',
      'Affiliate Promotion Plan:',
      formData.promotionPlan,
      '',
      'Additional Notes:',
      formData.additionalNotes || 'None provided'
    ];

    const mailtoLink = `mailto:hello@relaywork.co?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`;

    toast.success('Opening your email client to finish submission...');
    window.location.href = mailtoLink;
  };

  const renderInput = (props) => {
    const { name, label, type = 'text', placeholder, required = false } = props;
    return (
      <div>
        <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-2">
          {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <input
          id={name}
          name={name}
          type={type}
          value={formData[name]}
          onChange={handleChange}
          placeholder={placeholder}
          className={`w-full rounded-xl border px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors[name] ? 'border-rose-400 focus:ring-rose-400' : 'border-gray-200'}`}
        />
        {errors[name] && <p className="mt-1 text-sm text-rose-500">{errors[name]}</p>}
      </div>
    );
  };

  const renderTextarea = (props) => {
    const { name, label, rows = 4, placeholder, required = false } = props;
    return (
      <div>
        <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-2">
          {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <textarea
          id={name}
          name={name}
          rows={rows}
          value={formData[name]}
          onChange={handleChange}
          placeholder={placeholder}
          className={`w-full rounded-xl border px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors[name] ? 'border-rose-400 focus:ring-rose-400' : 'border-gray-200'}`}
        />
        {errors[name] && <p className="mt-1 text-sm text-rose-500">{errors[name]}</p>}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-gray-100 py-16">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/affiliates" className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-500">
          <span aria-hidden>&larr;</span>
          <span className="ml-2">Back to Affiliate Program</span>
        </Link>

        <div className="mt-8 bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-emerald-500 px-8 py-10 text-white">
            <h1 className="text-3xl md:text-4xl font-extrabold mb-3">Alfred Voice Affiliate Application</h1>
            <p className="text-lg text-indigo-100/90 max-w-2xl">
              Tell us about your audience and promotional strategy so we can set you up with the right campaign resources and recurring commission structure.
            </p>
          </div>

          <div className="px-8 py-10">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid md:grid-cols-2 gap-6">
                {renderInput({
                  name: 'fullName',
                  label: 'Full name',
                  placeholder: 'Jamie Rivera',
                  required: true
                })}
                {renderInput({
                  name: 'email',
                  label: 'Work email',
                  type: 'email',
                  placeholder: 'you@company.com',
                  required: true
                })}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {renderInput({
                  name: 'company',
                  label: 'Company or organization',
                  placeholder: 'Relay Work Media',
                  required: true
                })}
                {renderInput({
                  name: 'website',
                  label: 'Primary website or social URL',
                  placeholder: 'https://example.com'
                })}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {renderInput({
                  name: 'audienceSize',
                  label: 'Audience reach (monthly)',
                  placeholder: 'e.g. 120K site visitors / 35K email list',
                  required: true
                })}
                {renderInput({
                  name: 'timeframe',
                  label: 'Planned launch timeframe',
                  placeholder: 'e.g. Q1 2025'
                })}
              </div>

              {renderTextarea({
                name: 'audienceDescription',
                label: 'Tell us about your audience',
                rows: 4,
                placeholder: 'Industry focus, demographics, regions, and buyer personas you serve.',
                required: true
              })}

              {renderTextarea({
                name: 'promotionChannels',
                label: 'Primary promotion channels',
                rows: 3,
                placeholder: 'List your core channels (email list, LinkedIn, YouTube, podcasts, paid media, etc.)'
              })}

              {renderTextarea({
                name: 'promotionPlan',
                label: 'Affiliate promotion plan',
                rows: 5,
                placeholder: 'Outline how you intend to promote Alfred Voice over the 12-month commission period. Include launch ideas, campaign cadence, and any assets you may need.',
                required: true
              })}

              {renderTextarea({
                name: 'additionalNotes',
                label: 'Additional notes or questions',
                rows: 4,
                placeholder: 'Share anything else that will help us evaluate your application.'
              })}

              <div className="flex items-center justify-between flex-wrap gap-4">
                <p className="text-sm text-gray-500">
                  Submitting will open your email client with the details pre-filled to <span className="font-semibold text-gray-700">hello@relaywork.co</span>.
                </p>
                <button
                  type="submit"
                  className="inline-flex items-center bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:bg-indigo-700 hover:-translate-y-0.5 transition-all"
                >
                  Review &amp; Email Application
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateApplicationPage;
